import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Button,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  Checkbox,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import {
  Building24Regular,
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
  ChevronRight24Regular,
  ChevronDown24Regular,
  Add20Regular,
  Delete20Regular,
  ArrowSync20Regular,
  ArrowClockwise24Regular,
  ArrowLeft20Regular,
} from "@fluentui/react-icons";
import { useAppStyles } from "../styles/appStyles";
import { useDevice } from "../../model/DeviceContext";
import { Company, Eup } from "../../model/DeviceTypes";
import UpdateCompanyPage from "./UpdateCompanyPage";
import RefreshEntitiesPage from "./RefreshEntitiesPage";
import AddCompanyPage from "./bdoentities/AddCompanyPage";
import DeleteCompanyPage from "./bdoentities/DeleteCompanyPage";

interface BDOEntitiesPageProps {
  onBack: () => void;
}

interface DataGridItem {
  eupId: string;
  companyId: string;
  companyName: string;
  eupCount: number;
  eupName: string;
  identificationNumberPrefix: string;
  isActive: boolean;
  isGroupHeader?: boolean;
}

const useLocalStyles = makeStyles({
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  activeStatus: {
    color: tokens.colorPaletteGreenForeground1,
  },
  inactiveStatus: {
    color: tokens.colorPaletteRedForeground1,
  },
  tooltipContentActive: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: '4px 8px',
    margin: '-4px -8px',
    borderRadius: '3px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
  },
  tooltipContentInactive: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: '4px 8px',
    margin: '-4px -8px',
    borderRadius: '3px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
  },
  groupRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: tokens.fontWeightSemibold,
    cursor: "pointer",
    fontSize: tokens.fontSizeBase200,
  },
  eupRow: {
    paddingLeft: tokens.spacingHorizontalXXL,
    fontSize: tokens.fontSizeBase200,
  },
  chevronButton: {
    display: "inline-flex",
    alignItems: "center",
    marginRight: tokens.spacingHorizontalS,
    cursor: "pointer",
  },
  chevronIcon: {
    width: "20px",
    height: "20px",
    minWidth: "20px",
    minHeight: "20px",
    flexShrink: 0,
  },
  statusColumn: {
    width: "40px",
    minWidth: "40px",
    maxWidth: "40px",
    textAlign: "right",
    display: "flex",
    justifyContent: "flex-end",
  },
  statusColumnHeader: {
    width: "40px",
    minWidth: "40px",
    maxWidth: "40px",
    textAlign: "right",
    "& > *": {
      display: "none",
    },
  },
  checkboxColumn: {
    width: "40px",
    minWidth: "40px",
    maxWidth: "40px",
  },
  checkboxColumnHeader: {
    width: "40px",
    minWidth: "40px",
    maxWidth: "40px",
    "& > *": {
      display: "none",
    },
  },
  dataGridContainer: {
    width: "100%",
    margin: 0,
    padding: 0,
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  toolbar: {
    padding: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  toolbarButton: {
    minWidth: "40px",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    fontSize: tokens.fontSizeBase200,
    "& > span": {
      fontSize: tokens.fontSizeBase200,
    },
  },
  addIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: "12px",
  },
  updateIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: "12px",
  },
  deleteIcon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: "12px",
  },
  refreshIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: "12px",
  },
  backIcon: {
    color: tokens.colorNeutralForeground1,
    fontSize: "12px",
  },
  trialMessageBar: {
    marginBottom: tokens.spacingVerticalM,
  },
  externalLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: "underline",
    cursor: "pointer",
  },
  eupCountContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
  },
  identificationPrefix: {
    fontStyle: "italic",
  },
});

const BDOEntitiesPage: React.FC<BDOEntitiesPageProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const styles = useAppStyles();
  const localStyles = useLocalStyles();
  const { subscription, refreshSubscription, fingerprint, isTrial, addCompanyToTrialSubscription } = useDevice();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [showUpdateCompanyPage, setShowUpdateCompanyPage] = useState(false);
  const [showRefreshProgress, setShowRefreshProgress] = useState(false);
  const [showAddCompanyPage, setShowAddCompanyPage] = useState(false);
  const [showDeleteCompanyPage, setShowDeleteCompanyPage] = useState(false);

  // Toggle company selection
  const toggleCompanySelection = (companyId: string) => {
    setSelectedCompany((prev) => {
      // If clicking the same company, deselect it
      if (prev === companyId) {
        return null;
      }
      // Otherwise select the new company
      return companyId;
    });
  };

  // Toolbar action handlers
  const handleAddCompany = () => {
    console.log('🏢 Navigate to add company page');
    setShowAddCompanyPage(true);
  };

  const handleAddCompanyComplete = async () => {
    console.log('✅ Add company complete');
    setShowAddCompanyPage(false);
  };

  const handleDeleteCompanies = () => {
    if (!selectedCompany || !subscription?.companies) return;

    const company = subscription.companies.find(c => c.companyId === selectedCompany);
    if (!company) return;

    console.log('🗑️ Navigate to delete company page. Selected:', selectedCompany, company.name);
    setShowDeleteCompanyPage(true);
  };

  const handleDeleteCompanyComplete = async () => {
    console.log('✅ Delete company complete');
    setShowDeleteCompanyPage(false);
    setSelectedCompany(null); // Deselect company after deletion
  };

  const handleUpdateCompany = () => {
    if (selectedCompany) {
      console.log('Update company clicked. Selected:', selectedCompany);
      setShowUpdateCompanyPage(true);
    }
  };

  const handleUpdateCompanyComplete = async () => {
    console.log('✅ Update company complete, refreshing subscription...');
    setShowUpdateCompanyPage(false);
    setSelectedCompany(null); // Deselect company after update
    await refreshSubscription();
  };

  const handleRefreshEntities = () => {
    console.log('🔄 Refresh BDO Entities clicked');
    setShowRefreshProgress(true);
  };

  const handleRefreshComplete = async () => {
    console.log('✅ Refresh complete');
    setShowRefreshProgress(false);
  };

  // Toggle group expansion
  const toggleGroup = (companyId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  // Prepare flat list with group headers
  const items = useMemo(() => {
    if (!subscription?.companies) return [];

    const result: DataGridItem[] = [];

    // Sort companies alphabetically by name
    const sortedCompanies = [...subscription.companies].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    sortedCompanies.forEach((company: Company) => {
      // Add group header row
      result.push({
        eupId: `group-${company.companyId}`,
        companyId: company.companyId,
        companyName: company.name,
        eupCount: company.eups.length,
        eupName: '',
        identificationNumberPrefix: '',
        isActive: false,
        isGroupHeader: true,
      });

      // Add EUP rows only if group is expanded
      if (expandedGroups.has(company.companyId)) {
        // Sort EUPs numerically by identificationNumberPrefix
        const sortedEups = [...company.eups].sort((a, b) => {
          const numA = parseInt(a.identificationNumberPrefix, 10) || 0;
          const numB = parseInt(b.identificationNumberPrefix, 10) || 0;
          if (numA !== numB) {
            return numA - numB;
          }
          return a.name.localeCompare(b.name);
        });

        sortedEups.forEach((eup: Eup) => {
          result.push({
            eupId: eup.eupId,
            companyId: company.companyId,
            companyName: company.name,
            eupCount: company.eups.length,
            eupName: eup.name,
            identificationNumberPrefix: eup.identificationNumberPrefix,
            isActive: eup.isActive,
            isGroupHeader: false,
          });
        });
      }
    });

    return result;
  }, [subscription, expandedGroups]);

  const columns: TableColumnDefinition<DataGridItem>[] = [
    createTableColumn<DataGridItem>({
      columnId: "checkbox",
      renderHeaderCell: () => "",
      renderCell: (item) => {
        if (item.isGroupHeader) {
          const isSelected = selectedCompany === item.companyId;
          return (
            <TableCellLayout>
              <Checkbox
                checked={isSelected}
                onChange={() => toggleCompanySelection(item.companyId)}
              />
            </TableCellLayout>
          );
        }
        return <TableCellLayout></TableCellLayout>;
      },
    }),
    createTableColumn<DataGridItem>({
      columnId: "name",
      compare: (a, b) => {
        // For group headers, sort by company name
        if (a.isGroupHeader && b.isGroupHeader) {
          return a.companyName.localeCompare(b.companyName);
        }
        // For EUP rows, sort numerically by identificationNumberPrefix first, then by name
        if (!a.isGroupHeader && !b.isGroupHeader) {
          const numA = parseInt(a.identificationNumberPrefix, 10) || 0;
          const numB = parseInt(b.identificationNumberPrefix, 10) || 0;
          if (numA !== numB) {
            return numA - numB;
          }
          return a.eupName.localeCompare(b.eupName);
        }
        // Mixed types, group headers come first
        return a.isGroupHeader ? -1 : 1;
      },
      renderHeaderCell: () => t("bdo_entities.column.name", "Podmiot BDO"),
      renderCell: (item) => {
        if (item.isGroupHeader) {
          const isExpanded = expandedGroups.has(item.companyId);
          return (
            <TableCellLayout>
              <div
                onClick={() => toggleGroup(item.companyId)}
                className={localStyles.chevronButton}
              >
                {isExpanded ? (
                  <ChevronDown24Regular className={localStyles.chevronIcon} />
                ) : (
                  <ChevronRight24Regular className={localStyles.chevronIcon} />
                )}
                <Text size={200}>{item.companyName}</Text>
              </div>
            </TableCellLayout>
          );
        }
        return (
          <TableCellLayout>
            <Text size={200}>
              <span className={localStyles.identificationPrefix}>{item.identificationNumberPrefix}</span> | {item.eupName}
            </Text>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<DataGridItem>({
      columnId: "status",
      renderHeaderCell: () => "",
      renderCell: (item) => {
        if (item.isGroupHeader) {
          return (
            <TableCellLayout>
              <div className={localStyles.eupCountContainer}>
                <Text size={200}>
                  {item.eupCount}
                </Text>
              </div>
            </TableCellLayout>
          );
        }
        return (
          <TableCellLayout>
            <div className={localStyles.statusBadge}>
              {item.isActive ? (
                <Tooltip
                  content={
                    <div className={localStyles.tooltipContentActive}>
                      {t("bdo_entities.status.active", "Aktywny")}
                    </div>
                  }
                  relationship="label"
                >
                  <CheckmarkCircle20Regular className={localStyles.activeStatus} />
                </Tooltip>
              ) : (
                <Tooltip
                  content={
                    <div className={localStyles.tooltipContentInactive}>
                      {t("bdo_entities.status.inactive", "Nieaktywny")}
                    </div>
                  }
                  relationship="label"
                >
                  <DismissCircle20Regular className={localStyles.inactiveStatus} />
                </Tooltip>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
  ];

  const hasNoCompanies = !subscription?.companies || subscription.companies.length === 0;
  const hasCompanies = subscription?.companies && subscription.companies.length > 0;
  const canAddCompany = !isTrial || (isTrial && !hasCompanies);
  const canDeleteCompany = !isTrial && selectedCompany !== null;
  const canRefresh = !isTrial; // Trial version cannot refresh entities

  // If showing add company page, render it instead
  if (showAddCompanyPage) {
    return (
      <AddCompanyPage
        onBack={() => setShowAddCompanyPage(false)}
        onSuccess={handleAddCompanyComplete}
      />
    );
  }

  // If showing delete company page, render it instead
  if (showDeleteCompanyPage && selectedCompany && subscription?.companies) {
    const company = subscription.companies.find(c => c.companyId === selectedCompany);
    if (company) {
      return (
        <DeleteCompanyPage
          companyId={selectedCompany}
          companyName={company.name}
          onBack={() => setShowDeleteCompanyPage(false)}
          onSuccess={handleDeleteCompanyComplete}
        />
      );
    }
  }

  // If showing refresh progress, render RefreshEntitiesPage
  if (showRefreshProgress) {
    return (
      <RefreshEntitiesPage
        onComplete={handleRefreshComplete}
      />
    );
  }

  // If showing update company page, render it instead
  if (showUpdateCompanyPage && selectedCompany && fingerprint) {
    return (
      <UpdateCompanyPage
        companyId={selectedCompany}
        deviceFingerprint={fingerprint}
        onComplete={handleUpdateCompanyComplete}
      />
    );
  }

  return (
    <div className={styles.appPageContainer}>
      <div className={styles.appPageHeader}>
        <Building24Regular className={styles.appPageHeaderIcon} />
        <Text className={styles.appPageTitle}>
          {t("bdo_entities.title", "Podmioty BDO")}
        </Text>
      </div>
      <div className={localStyles.dataGridContainer}>
        <Toolbar className={localStyles.toolbar}>
          <Tooltip content={t("bdo_entities.toolbar.back_tooltip", "Powrót do poprzedniej strony")} relationship="description">
            <ToolbarButton

              className={localStyles.toolbarButton}
              aria-label={t("bdo_entities.toolbar.back", "Powrót")}
              icon={<ArrowLeft20Regular className={localStyles.backIcon} />}
              onClick={onBack}
            >
              {t("bdo_entities.toolbar.back", "Powrót")}
            </ToolbarButton>
          </Tooltip>
          <ToolbarDivider />
          <Tooltip content={
            !canAddCompany
              ? t("bdo_entities.toolbar.add_disabled_trial", "W wersji Trial możesz dodać tylko 1 firmę")
              : t("bdo_entities.toolbar.add_tooltip", "Dodaj nową firmę do subskrypcji")
          } relationship="description">
            <ToolbarButton
              className={localStyles.toolbarButton}
              aria-label={t("bdo_entities.toolbar.add", "Dodaj")}
              icon={<Add20Regular className={localStyles.addIcon} />}
              onClick={handleAddCompany}
              disabled={!canAddCompany}
            />
          </Tooltip>
          <Tooltip content={t("bdo_entities.toolbar.update_tooltip", "Aktualizuj dane wybranej firmy z systemu BDO")} relationship="description">
            <ToolbarButton
              className={localStyles.toolbarButton}
              aria-label={t("bdo_entities.toolbar.update", "Aktualizuj")}
              icon={<ArrowSync20Regular className={localStyles.updateIcon} />}
              onClick={handleUpdateCompany}
              disabled={!selectedCompany}
            />
          </Tooltip>
          <Tooltip content={
            isTrial
              ? t("bdo_entities.toolbar.delete_disabled_trial", "Usuwanie firm niedostępne w wersji Trial")
              : t("bdo_entities.toolbar.delete_tooltip", "Usuń wybraną firmę z subskrypcji")
          } relationship="description">
            <ToolbarButton
              className={localStyles.toolbarButton}
              aria-label={t("bdo_entities.toolbar.delete", "Usuń")}
              icon={<Delete20Regular className={localStyles.deleteIcon} />}
              onClick={handleDeleteCompanies}
              disabled={!canDeleteCompany}
            />
          </Tooltip>
          <ToolbarDivider />
          <Tooltip content={
            isTrial
              ? t("bdo_entities.toolbar.refresh_disabled_trial", "Odświeżanie niedostępne w wersji Trial")
              : t("bdo_entities.toolbar.refresh_tooltip", "Odśwież listę wszystkich firm i podmiotów BDO")
          } relationship="description">
            <ToolbarButton
              className={localStyles.toolbarButton}
              aria-label={t("bdo_entities.toolbar.refresh", "Odśwież")}
              icon={<ArrowClockwise24Regular className={localStyles.refreshIcon} />}
              onClick={handleRefreshEntities}
              disabled={!canRefresh}
            />
          </Tooltip>
        </Toolbar>

        {hasNoCompanies ? (
          <div className={styles.subscriptionContent}>
            <MessageBar intent="warning">
              <MessageBarBody>
                {t("bdo_entities.no_companies", "Brak firm w subskrypcji")}
              </MessageBarBody>
            </MessageBar>
          </div>
        ) : (
          <DataGrid
            items={items}
            columns={columns}
            sortable
            getRowId={(item) => item.eupId}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell, columnId }) => (
                  <DataGridHeaderCell
                    className={
                      columnId === "status" ? localStyles.statusColumnHeader :
                        columnId === "checkbox" ? localStyles.checkboxColumnHeader :
                          undefined
                    }
                  >
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<DataGridItem>>
              {({ item, rowId }) => (
                <DataGridRow<DataGridItem>
                  key={rowId}
                  className={item.isGroupHeader ? localStyles.groupRow : localStyles.eupRow}
                >
                  {({ renderCell, columnId }) => (
                    <DataGridCell
                      className={
                        columnId === "status" ? localStyles.statusColumn :
                          columnId === "checkbox" ? localStyles.checkboxColumn :
                            undefined
                      }
                    >
                      {renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        )}

        <div className={styles.subscriptionContent}>
          {isTrial && (
            <MessageBar intent="info" className={localStyles.trialMessageBar}>
              <MessageBarBody>
                {t("bdo_entities.trial_limit", "W wersji Trial możesz dodać tylko 1 firmę. Aby dodać więcej firm, aktywuj pełną wersję aplikacji.")}
              </MessageBarBody>
            </MessageBar>
          )}
          <Text>
            {t("bdo_entities.info.title", "Aby dodać firmę do subskrypcji, potrzebujesz kluczy API (Client ID i Client Secret) z systemu BDO dla każdej firmy.")}
          </Text>
          <br />
          <Text>
            {t("bdo_entities.info.instructions", "Szczegóły jak je wygenerować znajdziesz w")} {" "}
            <a
              href="https://bdo.mos.gov.pl/wp-content/uploads/2023/04/BDO_IS_Instrukcja-generowania-kluczy-API.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className={localStyles.externalLink}
            >
              {t("bdo_entities.info.link", "oficjalnej instrukcji BDO")}
            </a>.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default BDOEntitiesPage;
